import React, { useState } from "react";
import { useApp } from "../contexts/AppContext";
import { useAccount } from "../contexts/AccountContext";
import { useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { LogIn, UserPlus } from "lucide-react";
import TermsAcceptance from "../components/TermsAcceptance";
import SocialAuthButtons from "../components/SocialAuthButtons";
import SiteButton from "../components/SiteButton";

export default function AccountAuth() {
  const { lang, settings } = useApp();
  const { account, ready, setSession } = useAccount();
  const navigate = useNavigate();
  const tr = (pt, en) => (lang !== "en" ? pt : en);

  const emailEnabled = settings?.account_email_enabled !== false;
  const googleOn = !!process.env.REACT_APP_GOOGLE_CLIENT_ID && settings?.account_google_enabled !== false;
  const fbOn = !!process.env.REACT_APP_FACEBOOK_APP_ID && settings?.account_facebook_enabled !== false;
  const socialOn = googleOn || fbOn;

  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "teacher" });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (ready && account) return <Navigate to="/account/dashboard" replace />;

  const inputCls = "w-full px-4 py-3 rounded-xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito text-[#0B1D3A] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!accepted) {
          toast.error(tr("Tem de aceitar os Termos e a Política de Privacidade.", "You must accept the Terms and Privacy Policy."));
          setBusy(false);
          return;
        }
        const { data } = await api.post("/account/register", { ...form, accept_terms: true });
        setSession(data.token, data.account);
        toast.success(tr("Conta criada!", "Account created!"));
        navigate("/account/dashboard");
      } else {
        const { data } = await api.post("/account/login", { email: form.email, password: form.password });
        setSession(data.token, data.account);
        toast.success(tr("Sessão iniciada!", "Logged in!"));
        navigate("/account/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Ocorreu um erro", "An error occurred"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="account-auth-page">
      <div className="bg-white rounded-3xl shadow-xl border border-[#0B1D3A]/5 p-8">
        <h1 className="font-fredoka text-3xl font-bold text-[#0B1D3A] text-center">
          {tr("Área de Professores e Encarregados", "Teachers & Parents Area")}
        </h1>
        <p className="text-center text-[#0B1D3A]/60 mt-1 mb-6 font-nunito text-sm">
          {tr("Entre ou crie a sua conta", "Sign in or create your account")}
        </p>

        <div className="flex gap-1 bg-[#0B1D3A]/[0.05] rounded-full p-1 mb-6">
          {emailEnabled && [["login", tr("Entrar", "Sign in")], ["register", tr("Criar conta", "Register")]].map(([m, lbl]) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`account-tab-${m}`}
              className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-[#FF6B6B] text-white" : "text-[#0B1D3A]/60"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {emailEnabled && (
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <>
              <input className={inputCls} placeholder={tr("Nome completo", "Full name")} required
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" />
              <div className="flex gap-1 bg-[#0B1D3A]/[0.05] rounded-xl p-1">
                {[["teacher", tr("Professor(a)", "Teacher")], ["parent", tr("Encarregado(a)", "Parent")]].map(([r, lbl]) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, role: r })} data-testid={`account-role-${r}`}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${form.role === r ? "bg-white shadow text-[#0B1D3A]" : "text-[#0B1D3A]/50"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </>
          )}
          <input className={inputCls} type="email" placeholder="Email" required
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email" />
          <input className={inputCls} type="password" placeholder={tr("Palavra-passe", "Password")} required minLength={6}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="account-password" />

          {mode === "register" && (
            <TermsAcceptance checked={accepted} onChange={setAccepted} lang={lang} testId="account-terms" />
          )}

          <SiteButton type="submit" disabled={busy} testId="account-submit" color="#1B5E3A"
            icon={mode === "register" ? UserPlus : LogIn}>
            {busy ? tr("Aguarde...", "Please wait...") : mode === "register" ? tr("Criar conta", "Create account") : tr("Entrar", "Sign in")}
          </SiteButton>
        </form>
        )}

        {emailEnabled && socialOn && (
          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-[#0B1D3A]/10" />
            <span className="text-xs text-[#0B1D3A]/40 font-nunito">{tr("ou continue com", "or continue with")}</span>
            <span className="h-px flex-1 bg-[#0B1D3A]/10" />
          </div>
        )}
        <SocialAuthButtons />
        {!emailEnabled && !socialOn && (
          <p className="text-center text-sm text-[#0B1D3A]/50 font-nunito" data-testid="account-disabled-msg">
            {tr("O início de sessão está temporariamente indisponível.", "Sign in is temporarily unavailable.")}
          </p>
        )}
      </div>
    </div>
  );
}
